import * as React from "react";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { cn } from "../cn";

export interface TrendStatProps {
  label: React.ReactNode;
  value: React.ReactNode;
  /** Percent change vs the previous period. null = no baseline to compare. */
  deltaPct?: number | null;
  /**
   * When true (default), a positive delta is rendered green and negative red.
   * Set false for metrics where "up" is bad (not used yet, kept for reuse).
   */
  higherIsBetter?: boolean;
  /** Optional caption under the value (e.g. "so với kỳ trước"). */
  sub?: React.ReactNode;
  /** Accent the left edge with the brand lime (use for the hero KPIs). */
  accent?: boolean;
}

/**
 * KPI card with a trend pill. Used in the dashboard summary strip. Delta arrow
 * + color come from the sign of `deltaPct`; null renders a neutral dash.
 */
export function TrendStat({
  label,
  value,
  deltaPct,
  higherIsBetter = true,
  sub,
  accent,
}: TrendStatProps) {
  const hasDelta = deltaPct != null && Number.isFinite(deltaPct);
  const up = hasDelta && (deltaPct as number) > 0;
  const down = hasDelta && (deltaPct as number) < 0;
  const good = up ? higherIsBetter : down ? !higherIsBetter : null;

  const Icon = up ? ArrowUpRight : down ? ArrowDownRight : Minus;
  const toneClass =
    good == null
      ? "text-ash bg-mist"
      : good
        ? "text-positive bg-positive/10"
        : "text-danger bg-danger/10";

  return (
    <div
      className={cn(
        "rounded-2xl border border-smoke bg-paper p-4",
        accent &&
          "relative overflow-hidden before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-lime",
      )}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wide text-ash">
        {label}
      </p>
      <div className="mt-1 flex items-end justify-between gap-2">
        <p className="text-2xl font-bold text-ink">{value}</p>
        {hasDelta ? (
          <span
            className={cn(
              "mb-0.5 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-bold",
              toneClass,
            )}
          >
            <Icon size={12} strokeWidth={2.5} />
            {Math.abs(deltaPct as number).toFixed(0)}%
          </span>
        ) : null}
      </div>
      {sub ? <p className="mt-0.5 text-[11px] text-ash">{sub}</p> : null}
    </div>
  );
}
